import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { FormPreviewShell } from '@/components/FormPreview/FormPreviewShell'
import {
  formPreviewModeLabel,
  formPreviewSourceLabel,
  type FormPreviewDevice,
} from '@/components/FormPreview/FormPreviewToolbar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type { FormPreviewSnapshot } from '@/contracts/form-preview'
import { Monitor, RefreshCw, Smartphone } from 'lucide-react'
import { useState } from 'react'

type FormPreviewDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  snapshot: FormPreviewSnapshot
}

export function FormPreviewDialog({ open, onOpenChange, snapshot }: FormPreviewDialogProps) {
  const [device, setDevice] = useState<FormPreviewDevice>('desktop')
  const [restartKey, setRestartKey] = useState(0)
  const mode = snapshot.mode

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="left-[10px] top-[10px] flex h-[calc(100dvh-20px)] max-h-none w-[calc(100dvw-20px)] max-w-none translate-x-0 translate-y-0 flex-col overflow-hidden p-0">
        <DialogHeader className="shrink-0 border-b border-slate-200 bg-white px-5 py-3 pr-14">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <DialogTitle className="truncate text-base">{snapshot.form.title}</DialogTitle>
                <Badge variant="outline">preview-only</Badge>
                <Badge variant="secondary">mock-safe</Badge>
              </div>
              <DialogDescription className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                <span>{formPreviewModeLabel[mode]}</span>
                <span>·</span>
                <span>{formPreviewSourceLabel[snapshot.source]}</span>
                <span>·</span>
                <span>含開始頁、題組、題目、外觀、邏輯與完成頁。</span>
              </DialogDescription>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <div className="inline-flex overflow-hidden rounded-md border border-slate-200 bg-slate-50 p-1">
                <Button
                  type="button"
                  size="sm"
                  variant={device === 'desktop' ? 'default' : 'ghost'}
                  onClick={() => setDevice('desktop')}
                >
                  <Monitor className="size-4" />
                  桌機
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={device === 'mobile' ? 'default' : 'ghost'}
                  onClick={() => setDevice('mobile')}
                >
                  <Smartphone className="size-4" />
                  手機
                </Button>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setRestartKey((current) => current + 1)}
              >
                <RefreshCw className="size-4" />
                重新開始
              </Button>
            </div>
          </div>
        </DialogHeader>
        <div className="min-h-0 flex-1 bg-slate-100 p-3">
          <FormPreviewShell
            key={restartKey}
            snapshot={snapshot}
            mode="full_flow"
            device={device}
            onDeviceChange={setDevice}
            showToolbar={false}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}
